<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="show"
        class="fixed inset-0 z-[1200] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-1 sm:p-4"
        @click.self="handleClose"
      >
        <div
          class="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col"
          @click.stop
        >
          <!-- Header -->
          <div class="flex items-start justify-between px-4 sm:px-6 pt-4 sm:pt-6 pb-3 border-b border-gray-100 flex-shrink-0">
            <div>
              <div class="inline-flex items-center gap-2 mb-2">
                <span class="text-xs font-semibold uppercase tracking-wide text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
                  Grupo Hive
                </span>
              </div>
              <h2 class="text-lg font-semibold text-gray-900 leading-snug">
                {{ colivingTitle || 'Solicitar información' }}
              </h2>
              <p class="text-sm text-gray-500 mt-1">
                Contacta con Grupo Hive a través de CoolLiving
              </p>
            </div>
            <button
              type="button"
              class="ml-4 flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
              @click="handleClose"
            >
              <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Success -->
          <div v-if="submitted" class="flex-1 flex flex-col items-center justify-center text-center px-6 py-10">
            <div class="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <svg class="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-gray-900 mb-2">¡Solicitud enviada!</h3>
            <p class="text-sm text-gray-500 max-w-xs">
              Grupo Hive recibirá tus datos y se pondrá en contacto contigo directamente.
            </p>
            <button
              type="button"
              class="mt-6 px-6 py-2.5 bg-indigo-600 text-white rounded-full text-sm font-medium hover:bg-indigo-700 transition-colors"
              @click="handleClose"
            >
              Cerrar
            </button>
          </div>

          <!-- Form -->
          <form
            v-else
            class="flex-1 overflow-y-auto scrollbar-hide px-4 sm:px-6 py-4"
            @submit.prevent="handleSubmit"
          >
            <fieldset class="mb-4">
              <legend class="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Tus datos</legend>
              <div class="space-y-3">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                  <input
                    v-model="form.applicantName"
                    type="text"
                    required
                    autocomplete="name"
                    placeholder="Tu nombre"
                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    v-model="form.applicantEmail"
                    type="email"
                    required
                    autocomplete="email"
                    placeholder="tu@email.com"
                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                  <input
                    v-model="form.applicantPhone"
                    type="tel"
                    autocomplete="tel"
                    placeholder="+34 600 000 000"
                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </fieldset>

            <fieldset class="mb-4">
              <legend class="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Estancia</legend>
              <div class="space-y-3">
                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Entrada *</label>
                    <input
                      v-model="form.desiredStartDate"
                      type="date"
                      required
                      :min="todayDate"
                      class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Duración (meses) *</label>
                    <select
                      v-model="form.stayDurationMonths"
                      required
                      class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Selecciona</option>
                      <option v-for="m in 11" :key="m" :value="m">{{ m }} {{ m === 1 ? 'mes' : 'meses' }}</option>
                    </select>
                  </div>
                </div>
                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Presupuesto máx. (€/mes)</label>
                    <input
                      v-model.number="form.budgetMax"
                      type="number"
                      min="0"
                      placeholder="800"
                      class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Ocupantes</label>
                    <select
                      v-model="form.occupants"
                      class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option :value="1">1 persona</option>
                      <option :value="2">2 personas</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Comentarios</label>
                  <textarea
                    v-model="form.comments"
                    rows="2"
                    placeholder="Cuéntanos qué buscas..."
                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                  />
                </div>
              </div>
            </fieldset>

            <label class="flex items-start gap-2 cursor-pointer mb-4">
              <input
                v-model="form.consentLeadSharing"
                type="checkbox"
                required
                class="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 flex-shrink-0"
              />
              <span class="text-xs text-gray-500 leading-relaxed">
                Acepto que mis datos se compartan con Grupo Hive para gestionar mi solicitud, conforme a la
                <router-link to="/privacy-policy" target="_blank" class="text-indigo-500 hover:underline">
                  política de privacidad
                </router-link>.
              </span>
            </label>

            <p v-if="errorMsg" class="text-sm text-red-600 mb-3">{{ errorMsg }}</p>

            <button
              type="submit"
              :disabled="isSubmitting || !form.consentLeadSharing"
              class="w-full py-3 bg-indigo-600 text-white rounded-full text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              <svg v-if="isSubmitting" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span>{{ isSubmitting ? 'Enviando...' : 'Enviar solicitud a Grupo Hive' }}</span>
            </button>
          </form>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  getLeadSessionProfile,
  profileToFormDefaults,
  submitHiveLead,
} from '../services/hiveLeadSubmit';

const props = defineProps({
  show: { type: Boolean, required: true },
  coliving: { type: Object, default: null },
  searchFilters: { type: Object, default: null },
});

const emit = defineEmits(['close', 'submitted']);

const submitted = ref(false);
const isSubmitting = ref(false);
const errorMsg = ref('');
const todayDate = new Date().toISOString().split('T')[0];

const defaultForm = () => ({
  applicantName: '',
  applicantEmail: '',
  applicantPhone: '',
  desiredStartDate: '',
  stayDurationMonths: '',
  budgetMax: null,
  occupants: 1,
  comments: '',
  consentLeadSharing: false,
});

const form = ref(defaultForm());

const colivingTitle = computed(() => {
  if (!props.coliving) return '';
  return props.coliving.title || props.coliving.propertyTitle || '';
});

const displayCity = computed(() =>
  props.coliving?.propertyLocation || props.coliving?.city || '',
);

function syncAuthEmail() {
  const user = getAuth().currentUser;
  if (user?.email && !form.value.applicantEmail) {
    form.value.applicantEmail = user.email;
  }
  if (user?.displayName && !form.value.applicantName) {
    form.value.applicantName = user.displayName;
  }
}

watch(
  () => props.show,
  (isOpen) => {
    if (!isOpen) return;
    submitted.value = false;
    errorMsg.value = '';
    const sessionDefaults = profileToFormDefaults(getLeadSessionProfile());
    form.value = sessionDefaults
      ? { ...defaultForm(), ...sessionDefaults }
      : defaultForm();
    syncAuthEmail();
    if (props.searchFilters?.priceMax && !form.value.budgetMax) {
      form.value.budgetMax = props.searchFilters.priceMax;
    }
  },
);

onAuthStateChanged(getAuth(), () => {
  if (props.show) syncAuthEmail();
});

function handleClose() {
  if (isSubmitting.value) return;
  emit('close');
}

async function handleSubmit() {
  errorMsg.value = '';
  if (!props.coliving?.id) {
    errorMsg.value = 'Propiedad no válida.';
    return;
  }
  if (!form.value.applicantName.trim()) {
    errorMsg.value = 'Introduce tu nombre.';
    return;
  }
  if (!form.value.applicantEmail.trim()) {
    errorMsg.value = 'Introduce tu email.';
    return;
  }
  if (!form.value.desiredStartDate) {
    errorMsg.value = 'Selecciona la fecha de entrada.';
    return;
  }
  if (!form.value.stayDurationMonths) {
    errorMsg.value = 'Selecciona la duración de la estancia.';
    return;
  }

  isSubmitting.value = true;
  try {
    const result = await submitHiveLead(
      props.coliving.id,
      form.value,
      {
        city: displayCity.value,
        searchFilters: props.searchFilters || null,
      },
    );

    if (!result.ok) {
      const firstError = Array.isArray(result.data?.errors) ? result.data.errors[0]?.message : null;
      errorMsg.value = result.data?.error || firstError || 'No se pudo enviar la solicitud.';
      return;
    }

    submitted.value = true;
    emit('submitted', result.leadId);
  } catch {
    errorMsg.value = 'Error de conexión. Inténtalo de nuevo.';
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.scrollbar-hide {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>
